import { useState, useEffect, useRef } from 'react'
import { Printer, Download, FileText, Loader2, CheckCircle2 } from 'lucide-react'
import api from '../../services/api'
import { formatDateTime } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

/**
 * Renders the merged contract document inline as HTML, with print + download
 * controls and a signature-audit footer showing who signed and when.
 *
 * Backend: GET /contracts/{id}/preview  (returns { html, ...signature meta })
 */

// Split the merged contract HTML into "pages" at each Annexure heading, so the on-screen
// view reads as separate sheets (like the downloaded document) rather than one long scroll.
// The document's real page breaks are before Annexure A / B / C; the main agreement is the
// first sheet and each Annexure gets its own.
function splitIntoContractPages(html) {
  if (!html) return []
  const parts = html.split(/(?=<(?:p|h1|h2|h3)[^>]*>\s*(?:<strong>|<b>)?\s*Annexure\s+[A-C])/i)
  return parts.map(s => s.trim()).filter(Boolean)
}

export default function ContractDocumentViewer({ contractId, contractStatus, userRole, onLoaded }) {
  const { t } = useTranslation('consultants')
  // Download/Print is available for any generated contract (not just after signing) — a
  // consultant needs to download & print it to sign manually, then upload the signed copy.
  // The backend still enforces who may access it and masks another consultant's PII.
  const canDownloadOrPrint = contractStatus && contractStatus !== 'Draft'
  const [html, setHtml] = useState(null)
  const [meta, setMeta] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const printableRef = useRef(null)

  const load = () => {
    setLoading(true)
    setError(null)
    api.get(`/contracts/${contractId}/preview`)
      .then(r => {
        setHtml(r.data.html || '')
        setMeta(r.data || {})
        onLoaded?.(r.data)
      })
      .catch(e => setError(e.response?.data?.detail || 'Failed to load contract preview'))
      .finally(() => setLoading(false))
  }

  // Re-fetch whenever the contract's STATUS changes too, not just when contractId changes —
  // otherwise signing (Pending Review -> Pending Signature -> Active) never refreshes this
  // preview, so the just-stored signed name/signature image appear to "disappear": the parent
  // ContractDetail re-fetches the CONTRACT record correctly, but this child kept showing the
  // stale pre-sign render since its effect never re-ran.
  useEffect(() => { load() }, [contractId, contractStatus])

  const print = () => {
    if (!printableRef.current) return
    const printContent = printableRef.current.innerHTML
    const w = window.open('', '_blank', 'width=800,height=900')
    w.document.write(`
      <html>
        <head>
          <title>Contract ${meta.contract_number || ''}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: 'Times New Roman', serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #000; line-height: 1.65; font-size: 11pt; }
            p { margin: 0.5em 0; text-align: justify; }
            h1, h2, h3 { font-weight: bold; margin: 1em 0 0.4em; }
            h1 { font-size: 14pt; text-align: center; text-decoration: underline; }
            h2 { font-size: 12pt; text-align: center; text-decoration: underline; }
            h3 { font-size: 11pt; text-decoration: underline; }
            table { border-collapse: collapse; width: 100%; margin: 0.8em 0; }
            td, th { border: 1px solid #000; padding: 6px 10px; vertical-align: top; text-align: left; }
            th { background: #fff; font-weight: bold; text-align: center; }
            ol, ul { padding-left: 1.8em; margin: 0.3em 0; }
            li { text-align: justify; margin: 0.2em 0; }
            .sig-footer { margin-top: 2em; padding-top: 1em; border-top: 2px solid #000; font-size: 10pt; }
            @page { margin: 1in; size: A4; }
            /* Each rendered sheet prints on its own page */
            .contract-page { page-break-after: always; }
            .contract-page:last-child { page-break-after: auto; }
            p[data-annexure], h1[data-annexure], h2[data-annexure] { page-break-before: always; break-before: page; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `)
    w.document.close()
    setTimeout(() => { w.focus(); w.print() }, 250)
  }

  const downloadDocx = async () => {
    setDownloading(true)
    try {
      // Endpoint now returns the file directly (always fresh, fully populated), with a
      // real filename (the contract number, e.g. "3P-CTR-00023.docx") in Content-Disposition
      // — read that instead of hardcoding "contract_{id}.docx", which showed as the
      // meaningless "contract_18.docx" regardless of the contract's real number.
      const r = await api.get(`/contracts/${contractId}/download`, { responseType: 'blob' })
      const disposition = r.headers?.['content-disposition'] || ''
      const match = /filename="([^"]+)"/.exec(disposition)
      const filename = match ? match[1] : `${(meta.contract_number || 'contract').replace(/\//g, '-')}.docx`
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      setError(e.response?.data?.detail || 'Download failed.')
    } finally {
      setDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin inline mr-2" />
        <span className="text-sm text-gray-500">{t('loading_contract_doc')}</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">
        {error}
        <button onClick={load} className="ml-3 text-blue-700 hover:underline">{t('retry')}</button>
      </div>
    )
  }

  const consultantSigned = !!meta.consultant_signed_name
  const adminSigned = !!meta.admin_signed_name
  const pages = splitIntoContractPages(html)

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <FileText className="w-4 h-4 text-blue-600" />
          Contract Document — please review the full document below
        </div>
        <div className="flex items-center gap-2">
          {canDownloadOrPrint ? (
            <>
              <button
                onClick={print}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-700"
              >
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
              <button
                onClick={downloadDocx}
                disabled={downloading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-700 disabled:opacity-50"
              >
                <Download className="w-3.5 h-3.5" /> {downloading ? 'Loading…' : 'Download .docx'}
              </button>
            </>
          ) : (
            <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              🔒 Download & Print available after company counter-signs
            </span>
          )}
        </div>
      </div>

      {/* Document body — rendered as separate A4 "sheets" so it reads page-by-page like the download */}
      <div className="px-4 py-6 max-h-150 overflow-y-auto bg-gray-100" ref={printableRef}>
        {/* Professional contract CSS — matches the original Word document formatting */}
        <style>{`
          /* Each logical page is a white A4 sheet on a grey backdrop */
          .contract-page {
            background: #fff;
            width: 100%;
            max-width: 794px;              /* A4 width @ 96dpi */
            margin: 0 auto 22px;
            padding: 54px 64px;
            border: 1px solid #e5e7eb;
            box-shadow: 0 1px 5px rgba(0,0,0,.14);
          }
          .contract-body {
            font-family: 'Times New Roman', 'Georgia', serif;
            color: #000;
            line-height: 1.6;
            font-size: 11pt;
            max-width: none;
            margin: 0;
          }
          /* All body text: justified like Word */
          .contract-body p {
            margin: 0.5em 0;
            text-align: justify;
            text-justify: inter-word;
          }
          /* Headings — centred, underlined as in the original */
          .contract-body h1 { font-size: 13pt; text-align: center; font-weight: bold; text-decoration: underline; margin: 1em 0 0.4em; }
          .contract-body h2 { font-size: 12pt; text-align: center; font-weight: bold; text-decoration: underline; margin: 0.8em 0 0.3em; }
          .contract-body h3 { font-size: 11pt; font-weight: bold; text-decoration: underline; margin: 0.6em 0 0.2em; }
          /* Bold inline content */
          .contract-body strong { font-weight: bold; }
          /* Numbered and bulleted lists — indented left-aligned */
          .contract-body ol, .contract-body ul { padding-left: 1.8em; margin: 0.4em 0; }
          .contract-body li { margin: 0.3em 0; text-align: justify; }
          /* ── TABLES ── */
          .contract-body table { border-collapse: collapse; width: 100%; margin: 0.8em 0; font-size: 10.5pt; }
          .contract-body td, .contract-body th { border: 1px solid #000; padding: 6px 10px; vertical-align: top; }
          /* Annexure A — milestones table */
          .contract-body th { font-weight: bold; text-align: center; background: #fff; }
          /* Annexure B — bank details: label left, value right area */
          .contract-body td { text-align: left; }
          /* "The Company" / "The Freelancer" bold in first col */
          .contract-body td:first-child:not(:only-child) { font-weight: bold; width: 40%; }
          /* Amount column right-aligned */
          .contract-body table td:last-child { text-align: left; }
          /* "Signed by:" centre */
          .contract-body p:has(strong) { text-align: left; }
          /* Section headings */
          .contract-body p > strong:only-child { display: block; font-size: 11pt; margin: 0.8em 0 0.2em; }
          /* Print: each sheet on its own page */
          @media print {
            .contract-page { box-shadow: none; border: none; margin: 0 auto; padding: 0; page-break-after: always; }
          }
        `}</style>
        {pages.map((pg, i) => (
          <div key={i} className="contract-page">
            <div className="contract-body" dangerouslySetInnerHTML={{ __html: pg }} />
          </div>
        ))}

        {/* ── Rich Signature Footer (Phase H) ─────────────────────────────
            Mirrors a real contract's signature page: each party has Name,
            Email, Date, typed signature in cursive, and the company side
            has an official seal. */}
        {(consultantSigned || adminSigned || meta.consultant_name) && (
          <div className="contract-page sig-footer text-sm">
            <h4 className="font-bold text-gray-800 mb-4 text-center tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
              SIGNATURES
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ── CONSULTANT BLOCK ── */}
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <p className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold mb-2">{t('for_the_consultant')}</p>
                <div className="space-y-1.5 text-xs">
                  <div><span className="text-gray-500">{t('name_label')}</span> <span className="font-medium text-gray-800">{meta.consultant_name || '—'}</span></div>
                  <div><span className="text-gray-500">{t('email_label')}</span> <span className="font-medium text-gray-800">{meta.consultant_email || '—'}</span></div>
                  <div><span className="text-gray-500">{t('date_label')}</span> <span className="font-medium text-gray-800">{meta.consultant_signed_at ? formatDateTime(meta.consultant_signed_at) : 'Pending'}</span></div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-300">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{t('signature')}</p>
                  {consultantSigned ? (
                    meta.consultant_signature_data ? (
                      <img src={meta.consultant_signature_data} alt="Consultant signature" className="h-14 object-contain" />
                    ) : (
                      <p className="text-2xl text-blue-900 italic font-bold" style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive' }}>
                        {meta.consultant_signed_name}
                      </p>
                    )
                  ) : (
                    <p className="text-xs text-gray-400 italic h-7 flex items-center">{t('awaiting_consultant_sig')}</p>
                  )}
                  {consultantSigned && (
                    <p className="text-[10px] text-green-600 flex items-center gap-1 mt-1">
                      <CheckCircle2 className="w-3 h-3" /> Verified e-signature
                    </p>
                  )}
                </div>
              </div>

              {/* ── COMPANY BLOCK ── */}
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200 relative">
                <p className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold mb-2">{t('for_the_company')}</p>
                <div className="space-y-1.5 text-xs">
                  <div><span className="text-gray-500">{t('company_label')}</span> <span className="font-medium text-gray-800">{meta.company_name || '3 Pillars Data & Training LLC'}</span></div>
                  <div><span className="text-gray-500">{t('signatory')}</span> <span className="font-medium text-gray-800">{meta.admin_name || '—'}</span></div>
                  <div><span className="text-gray-500">{t('title_label')}</span> <span className="font-medium text-gray-800">{meta.admin_title || 'Authorised Signatory'}</span></div>
                  <div><span className="text-gray-500">{t('email_label')}</span> <span className="font-medium text-gray-800">{meta.admin_email || '—'}</span></div>
                  <div><span className="text-gray-500">{t('date_label')}</span> <span className="font-medium text-gray-800">{meta.admin_signed_at ? formatDateTime(meta.admin_signed_at) : 'Pending'}</span></div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-300">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{t('signature')}</p>
                  {adminSigned ? (
                    meta.admin_signature_data ? (
                      <img src={meta.admin_signature_data} alt="Company signature" className="h-14 object-contain" />
                    ) : (
                      <p className="text-2xl text-blue-900 italic font-bold" style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive' }}>
                        {meta.admin_signed_name}
                      </p>
                    )
                  ) : (
                    <p className="text-xs text-gray-400 italic h-7 flex items-center">{t('awaiting_company_sig')}</p>
                  )}
                  {adminSigned && (
                    <p className="text-[10px] text-green-600 flex items-center gap-1 mt-1">
                      <CheckCircle2 className="w-3 h-3" /> Verified e-signature
                    </p>
                  )}
                </div>

                {/* CSS-rendered Official Seal — appears once admin counter-signs */}
                {adminSigned && (
                  <div className="absolute top-3 right-3 pointer-events-none select-none" style={{ transform: 'rotate(-12deg)' }}>
                    <div className="w-24 h-24 rounded-full border-[3px] border-blue-700 flex items-center justify-center text-center"
                         style={{ background: 'rgba(255,255,255,0.7)', boxShadow: '0 0 0 2px rgba(29,78,216,0.15)' }}>
                      <div className="text-[7px] font-bold text-blue-800 leading-tight tracking-wider uppercase px-1">
                        <div>3 Pillars Data</div>
                        <div className="text-[9px] my-0.5">★</div>
                        <div>{t('official_seal')}</div>
                        <div className="text-[6px] mt-0.5 opacity-70">Sharjah · UAE</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <p className="text-[10px] text-gray-400 text-center mt-4 italic">
              This contract was electronically signed via the ProTrack platform.
              Signature records include typed name, IP address, and timestamp captured at the moment of signing.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
